import React from 'react';
import { View, Text, Link } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../tokens/types';
import { PdfSection, PdfSparkBars } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenSparkBars } from '../primitives/ScreenPrimitives';
import { PdfLineChart, ScreenLineChart, type LineChartPoint } from '../primitives/ChartPrimitives';
import { overviewColors, overviewDesign, newsDesign, competitorsDesign, coverFonts, ptToPx, interSectionGap } from '../tokens/designTokens';
import { fmtMonthYear, fmtMonthYearShort } from './formatUtils';
import { usePdfBodyWidth } from '../tokens/pdfLayout';
import { stripHtml, splitBullets, truncateText, sourceInitials, sentimentTone } from '../tokens/overviewHelpers';

// ─── Color resolver ─────────────────────────────────────────────
// Tokens reference colour names (string keys). resolve() turns them into hex.
type ColorKey = keyof typeof overviewColors;
const c = (key: string): string => (overviewColors as Record<string, string>)[key] ?? key;

// ─── View model ─────────────────────────────────────────────────
type ChipTone = 'positive' | 'accent' | 'muted';
type Chip = { label: string; tone: ChipTone };
type SectorMetric = { label: string; value: string; unit?: string; sub?: string; valueColor?: string };

type OverviewVM = {
    initial: string;
    orgTitle: string;
    chips: Chip[];
    subtitle: string;
    aboutBullets?: string[];
    businessCards: Array<{ title: string; bullets: string[] }>;
    basicDetails: {
        items: Array<{ label: string; value: string; href?: string }>;
        address?: string;
    };
    news: Array<{
        url?: string;
        source: string;
        initials: string;
        sentiment: 'pos' | 'neg' | 'neutral';
        sentimentLabel: string;
        headline: string;
        date: string;
    }>;
    sectorOutlook?: {
        sectorName?: string;
        intro?: string;
        metrics: SectorMetric[];
        body?: string;
        growthDrivers: string[];
        keyRisks: string[];
    };
    competitors?: {
        focal: { name: string };
        rivals: Array<{ name: string }>;
        rows: Array<{ key: string; label: string; values: string[]; mono?: boolean }>;
        cards: Array<{
            name: string;
            kind: 'subject' | 'competitor';
            description?: string;
            truncated?: boolean;
            foundingYear?: string;
            location?: string;
            website?: string;
            keyAchievements?: string;
        }>;
    };
};

const buildVM = (data: ReportData): OverviewVM => {
    const o = data.overview;
    const id = data.identity;
    const score = data.score;

    const initial = (id.orgName ?? '?').trim()[0]?.toUpperCase() ?? '?';

    const chips: Chip[] = [];
    if (id.profileStatus) chips.push({ label: id.profileStatus, tone: 'positive' });
    if (id.startupSme) chips.push({ label: id.startupSme, tone: 'accent' });

    const subtitleParts: string[] = [];
    if (id.foundationDate) subtitleParts.push(`Founded ${fmtMonthYear(id.foundationDate)}`);
    if (id.sector) subtitleParts.push(id.sector);
    if (id.state) subtitleParts.push(id.state);
    const subtitle = subtitleParts.join(' · ');

    const aboutBullets = o?.overviewContent ? splitBullets(stripHtml(o.overviewContent)) : undefined;

    const businessCards: OverviewVM['businessCards'] = [];
    if (o?.productOfferings) businessCards.push({ title: 'Product & Offerings', bullets: splitBullets(stripHtml(o.productOfferings)) });
    if (o?.customersAndSalesChannels)
        businessCards.push({ title: 'Customers & Channels', bullets: splitBullets(stripHtml(o.customersAndSalesChannels)) });
    if (o?.geographiesSegmentation)
        businessCards.push({ title: 'Geographies', bullets: splitBullets(stripHtml(o.geographiesSegmentation)) });

    // Basic Details. `href` (optional per row) is rendered as a clickable
    // hyperlink in the PDF — used for the company website. Static rows like
    // PAN / CIN / GSTIN don't get a href so they render as plain mono text.
    const bdItems: Array<{ label: string; value: string; href?: string }> = [];
    if (id.pan && id.pan !== '—') bdItems.push({ label: 'PAN', value: id.pan });
    if (id.cin && id.cin !== '—') bdItems.push({ label: 'CIN', value: id.cin });
    const primaryGstin = id.gstins?.[0]?.gstin;
    if (primaryGstin) bdItems.push({ label: 'GSTIN', value: primaryGstin });
    if (id.websiteUrl) {
        const display = id.websiteUrl.replace(/^https?:\/\//i, '').replace(/\/$/, '');
        bdItems.push({ label: 'Website', value: display, href: id.websiteUrl });
    }
    const basicDetails = { items: bdItems, address: id.fullAddress };

    // News
    const news = (o?.news ?? []).slice(0, 4).map((n) => ({
        url: n.redirectUrl,
        source: truncateText(n.publisherName, 22),
        initials: sourceInitials(n.publisherName),
        sentiment: sentimentTone(n.sentiment),
        sentimentLabel: (n.sentiment ?? '').toUpperCase() || 'NEUTRAL',
        headline: truncateText(stripHtml(n.title), 90),
        date: fmtMonthYear(n.publishedDate) || n.publishedDate,
    }));

    // Sector outlook
    let sectorOutlook: OverviewVM['sectorOutlook'];
    if (o?.sectorOverview || o?.marketSize || o?.industryCagr || o?.marketGrowth || o?.sectorRisks) {
        const metrics: SectorMetric[] = [];
        if (o.marketSize) {
            const parsed = parseMetric(o.marketSize);
            metrics.push({ label: 'Market Size', ...parsed });
        }
        if (o.industryCagr) {
            const parsed = parseMetric(o.industryCagr);
            metrics.push({ label: 'Industry CAGR', ...parsed });
        }
        if (o.sectorRisks) {
            metrics.push({ label: 'Sector Risk', value: capFirst(o.sectorRisks), valueColor: riskValueColor(o.sectorRisks) });
        }
        if (o.marketGrowth) {
            const parsed = parseMetric(o.marketGrowth);
            metrics.push({ label: 'Market Growth', ...parsed });
        }
        sectorOutlook = {
            sectorName: id.sector,
            intro: o.sectoralPerformance ? stripHtml(o.sectoralPerformance) : undefined,
            metrics,
            body: o.sectorOverview ? stripHtml(o.sectorOverview) : undefined,
            growthDrivers: (o.growthDrivers ?? []).map(stripHtml).filter(Boolean).slice(0, 4),
            keyRisks: (o.keyRisks ?? []).map(stripHtml).filter(Boolean).slice(0, 4),
        };
    }

    // Competitors
    let competitors: OverviewVM['competitors'];
    // The Polus `competitors` payload tags the subject company itself as one
    // of the entries (description contains "Subject Company"). We prepend our
    // own focal card below, so we must remove the subject from the rival list
    // first — otherwise the same company renders twice (as Card 1 the subject,
    // again as Card 2 the first "rival"). Belt-and-suspenders: also filter by
    // name match against the subject org name in case the description tag is
    // missing for a given row.
    const subjectNameNorm = (id.orgName ?? '').trim().toLowerCase();
    const allCompetitors = (o?.competitors ?? []).filter((c) => {
        if (/subject\s+company/i.test(c.description ?? '')) return false;
        const name = (c.companyName ?? '').trim().toLowerCase();
        if (subjectNameNorm && name && name === subjectNameNorm) return false;
        return true;
    });
    if (allCompetitors.length > 0) {
        const focal = { name: id.orgName };
        const rivals = allCompetitors.slice(0, 3).map((cm) => ({ name: cm.companyName }));
        // The Polus competitor endpoint emits `keyAchievements` for rival
        // companies but the subject company comes from a separate
        // `companyOverview` endpoint that doesn't expose that field directly.
        // Synthesize one from existing signals so the subject card matches
        // the visual weight of its rivals: prefer the first recent trend
        // (the closest semantic match), fall back to a funding-raised
        // summary, then to the top-line product offering. Return undefined
        // when none of those produce content — leaving the slot empty is
        // better than printing filler.
        const synthesizeKeyAchievements = (): string | undefined => {
            const firstTrend = (o?.recentTrends ?? []).map(stripHtml).filter(Boolean)[0];
            if (firstTrend) return truncateText(firstTrend, 220);
            const funding = (o?.fundingRaised ?? '').trim();
            const topInvestors = (o?.keyInvestors ?? []).slice(0, 3).filter(Boolean).join(', ');
            if (funding && topInvestors) return `Raised ${funding} with backing from ${topInvestors}.`;
            if (funding) return `Total funding raised: ${funding}.`;
            const offering = o?.productOfferings ? stripHtml(o.productOfferings) : '';
            if (offering) return truncateText(offering, 220);
            return undefined;
        };
        const focalCompetitor = {
            companyName: id.orgName,
            description: aboutBullets?.join(' '),
            // Show just the state name (matches the AICA Overview tab). The
            // small competitor card can't fit a full HQ address — long strings
            // wrap awkwardly and steal space from the rest of the card.
            location: id.state,
            revenue: undefined as string | undefined,
            employees: data.management?.employeeRange,
            foundingYear: (() => {
                if (!id.foundationDate) return undefined;
                const y = new Date(id.foundationDate).getFullYear();
                return Number.isFinite(y) ? y.toString() : undefined;
            })(),
            totalFunding: o?.fundingRaised,
            fundingStage: id.startupSme,
            investors: (o?.keyInvestors ?? []).slice(0, 3).join(', '),
            website: id.websiteUrl,
            keyAchievements: synthesizeKeyAchievements(),
        };
        const cols = [focalCompetitor, ...allCompetitors.slice(0, 3)];
        const rowSpec: Array<{ key: string; label: string; mono?: boolean; pick: (cm: any) => string | undefined }> = [
            { key: 'about', label: 'About', pick: (cm) => cm.description },
            { key: 'location', label: 'Location', pick: (cm) => extractState(cm.location) },
            { key: 'revenue', label: 'Revenue', mono: true, pick: (cm) => cm.revenue },
            { key: 'employees', label: 'Employees', mono: true, pick: (cm) => cm.employees },
            { key: 'founded', label: 'Founded', mono: true, pick: (cm) => cm.foundingYear },
            { key: 'funding', label: 'Funding', mono: true, pick: (cm) => cm.totalFunding },
            // 'Stage' / 'Funding Stage' row removed per design feedback —
            // the value (id.startupSme) is rarely populated and read as
            // noise next to the other rows. Re-add the row spec entry to
            // surface it again.
            { key: 'investors', label: 'Investors', pick: (cm) => cm.investors },
        ];
        const rows = rowSpec
            .map((r) => ({
                key: r.key,
                label: r.label,
                mono: r.mono,
                values: cols.map((cm) => {
                    const v = r.pick(cm);
                    if (!v || v === '—' || v.toLowerCase() === 'nan') return 'Not disclosed';
                    return r.key === 'about' ? truncateText(v, 140) : v;
                }),
            }))
            .filter((r) => r.values.some((v) => v !== 'Not disclosed'));
        const cards = cols.map((cm, i) => {
            const dropFiller = (v?: string) => (!v || /not publicly disclosed|not disclosed|nan/i.test(v) ? undefined : v);
            // Truncate the description so the SUBJECT card (whose description is
            // the full About-bullets joined together — easily 1000+ chars) does
            // not overflow its wrap={false} card and spill into the next column.
            // Cap is generous enough for a real competitor description (~200
            // chars in the API), short enough to fit the card's `descMinHeight`
            // line budget. `truncateText` adds "…" as the explicit indicator;
            // the full About text already lives in the "About the Company"
            // section above, so a truncated marker here is reader-safe.
            const description = cm.description ? truncateText(cm.description, 260) : undefined;
            return {
                name: cm.companyName ?? '—',
                kind: (i === 0 ? 'subject' : 'competitor') as 'subject' | 'competitor',
                description,
                truncated: !!(cm.description && cm.description.length > 260),
                foundingYear: dropFiller(cm.foundingYear),
                location: dropFiller(extractState(cm.location)),
                website: dropFiller(cm.website),
                keyAchievements: dropFiller(cm.keyAchievements),
            };
        });
        competitors = { focal, rivals, rows, cards };
    }

    return {
        initial,
        orgTitle: id.orgName ?? '—',
        chips,
        subtitle,
        aboutBullets,
        businessCards,
        basicDetails,
        news,
        sectorOutlook,
        competitors,
    };
};

// Parse a sector-outlook metric value into its three visual parts:
//   - `value`: the headline number (mono medium, primary ink)
//   - `unit`:  the currency / scale (mono regular, muted ink, sits next to value)
//   - `sub`:   trailing context like "by 2028" or "(US$28 Bn)" — separate line, smaller
//
// Examples:
//   "Rs. 2,43,236 Cr (US$28 Bn)" → { value: 'Rs. 2,43,236', unit: 'Cr', sub: 'US$28 Bn' }
//   "₹ 5,179 Cr by 2028"         → { value: '₹ 5,179',      unit: 'Cr', sub: 'by 2028' }
//   "10-11% annually"            → { value: '10-11%',       sub: 'annually' }
//   "15%"                        → { value: '15%' }
// Replace currency / scale glyphs that GeistMono (the value font) lacks. When a
// font has no glyph for a code point, react-pdf either drops it to zero advance
// width (next char overlaps) or substitutes a tofu — either way the metric reads
// as garbage like "T9 billion" instead of "₹9 billion". Mono fonts in particular
// rarely ship Indian currency / extended Latin glyphs, so we ASCII-fy on the way
// in. Keep this list narrow; only add a symbol here after seeing it actually
// fail to render.
const sanitizeForMonoFont = (s: string): string =>
    s
        .replace(/₹\s*/g, 'Rs. ')
        .replace(/\s{2,}/g, ' ')
        .trim();

const parseMetric = (raw: string): { value: string; unit?: string; sub?: string } => {
    const trimmed = sanitizeForMonoFont(raw);

    // 1. Pull off any trailing parenthetical — that's always sub.
    const parenMatch = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    let main = trimmed;
    let sub: string | undefined;
    if (parenMatch) {
        main = parenMatch[1].trim();
        sub = parenMatch[2].trim();
    }

    // 2. Split off "VALUE UNIT [TRAILING SUB]" where unit is one of the known scale words.
    const unitMatch = main.match(/^(.+?)\s+(Cr|Crore|Lakh|Mn|Bn|Million|Billion)(?:\s+(.+))?$/i);
    if (unitMatch) {
        const value = unitMatch[1].trim();
        const unit = unitMatch[2];
        const trailing = unitMatch[3]?.trim();
        return { value, unit, sub: sub ?? trailing };
    }

    // 3. Percent at the end of the value, followed by trailing sub: "10-11% annually".
    const pctMatch = main.match(/^([\d.,\-\s]+%)\s+(.+)$/);
    if (pctMatch) {
        return { value: pctMatch[1].trim(), sub: sub ?? pctMatch[2].trim() };
    }

    return { value: main, sub };
};

const capFirst = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s);

// Reduce a long HQ location string (Polus shape "City, State, Country") to
// just the state. The Key Competitors card can't fit a full address — long
// strings wrap and steal vertical space from the rest of the card. Heuristic:
//   - strip trailing country tokens (India, USA, UK, etc.)
//   - if 2+ segments remain after the strip, return the last (state)
//   - if 1 segment remains, return as-is
//   - if the input is already short (<= ~22 chars), return as-is — it's
//     probably already just a state name (the focal card's source).
const COUNTRY_TOKENS = /^(india|usa|us|united states|uk|united kingdom|england|china|singapore|uae|canada|australia)$/i;
const extractState = (loc?: string): string | undefined => {
    if (!loc) return loc;
    const trimmed = loc.trim();
    if (trimmed.length <= 22 && !trimmed.includes(',')) return trimmed;
    const parts = trimmed
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (parts.length === 0) return trimmed;
    const withoutCountry = COUNTRY_TOKENS.test(parts[parts.length - 1]) ? parts.slice(0, -1) : parts;
    if (withoutCountry.length === 0) return parts[0];
    return withoutCountry[withoutCountry.length - 1];
};

const riskValueColor = (s: string): string => {
    const v = s.toLowerCase();
    if (v.includes('high') || v.includes('severe')) return overviewColors.neg;
    if (v.includes('medium')) return overviewColors.warn;
    return overviewColors.pos;
};

// ════════════════════════════════════════════════════════════════
//                            P D F
// ════════════════════════════════════════════════════════════════

const PF = coverFonts.pdf;
const od = overviewDesign;
const nd = newsDesign;
const cd = competitorsDesign;

const PdfH3: React.FC<{ children: React.ReactNode; topGap?: number }> = ({ children, topGap = od.sectionGap }) => (
    <Text
        style={{
            fontSize: od.h3Size,
            fontFamily: PF.sans,
            fontWeight: 600,
            color: c(od.h3Color),
            marginTop: topGap,
            marginBottom: od.headingToBody,
        }}
    >
        {children}
    </Text>
);

const PdfChipBox: React.FC<{ label: string; tone: ChipTone }> = ({ label, tone }) => {
    const tones: Record<ChipTone, { bg: string; fg: string }> = {
        positive: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        accent: { bg: overviewColors.accentSoft, fg: overviewColors.accentDeep },
        muted: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    };
    const t = tones[tone];
    // Three-part centering pattern (skill rule 9): lineHeight:1 on Text +
    // alignItems/justifyContent on View + asymmetric padding. Without all three,
    // react-pdf's baseline lands low and the chip text reads "too low".
    return (
        <View
            style={{
                backgroundColor: t.bg,
                paddingHorizontal: od.idChipPadX,
                paddingTop: 2,
                paddingBottom: 3,
                borderRadius: od.idChipRadius,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text
                style={{
                    fontSize: od.idChipSize,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    color: t.fg,
                    letterSpacing: od.idChipTrackingEm * od.idChipSize,
                    textTransform: 'uppercase',
                    lineHeight: 1,
                }}
            >
                {label}
            </Text>
        </View>
    );
};

const PdfIdentityCard: React.FC<{ vm: OverviewVM }> = ({ vm }) => (
    <View
        style={{
            backgroundColor: overviewColors.surface,
            borderRadius: od.idCardRadius,
            paddingHorizontal: od.idCardPadX,
            paddingVertical: od.idCardPadY,
            flexDirection: 'row',
            alignItems: 'center',
            gap: od.idCardGap,
        }}
        wrap={false}
    >
        <View
            style={{
                width: od.idLogoSize,
                height: od.idLogoSize,
                borderRadius: od.idLogoRadius,
                backgroundColor: overviewColors.ink,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text style={{ fontSize: od.idLogoTextSize, color: '#FFFFFF', fontFamily: PF.display, fontWeight: 400, lineHeight: 1 }}>
                {vm.initial}
            </Text>
        </View>
        <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: od.idChipGap, flexWrap: 'wrap' }}>
                <Text
                    style={{
                        fontSize: od.idTitleSize,
                        fontFamily: PF.display,
                        fontWeight: 400,
                        color: overviewColors.ink,
                        lineHeight: od.idTitleLineHeight,
                    }}
                >
                    {vm.orgTitle}
                </Text>
                {vm.chips.map((ch) => (
                    <PdfChipBox key={ch.label} label={ch.label} tone={ch.tone} />
                ))}
            </View>
            {vm.subtitle ? (
                <Text
                    style={{
                        fontSize: od.idSubtitleSize,
                        fontFamily: PF.sans,
                        color: c(od.idSubtitleColor),
                        marginTop: 3,
                        lineHeight: od.idSubtitleLineHeight,
                    }}
                >
                    {vm.subtitle}
                </Text>
            ) : null}
        </View>
    </View>
);

const PdfBusinessCard: React.FC<{ title: string; bullets: string[] }> = ({ title, bullets }) => (
    <View
        style={{
            flex: 1,
            backgroundColor: '#fafaf8',
            borderWidth: od.bpCardBorder,
            borderColor: overviewColors.border,
            borderRadius: od.bpCardRadius,
            padding: od.bpCardPad,
        }}
    >
        <Text
            style={{
                fontSize: od.bpTitleSize,
                fontFamily: PF.sans,
                fontWeight: 600,
                color: c(od.bpTitleColor),
                marginBottom: 5,
            }}
        >
            {title}
        </Text>
        {bullets.map((b, i) => (
            <View key={i} style={{ flexDirection: 'row', gap: 4, marginTop: i > 0 ? 4 : 0 }}>
                <Text style={{ fontSize: od.bpBodySize, fontFamily: PF.sans, color: c(od.bpBodyColor), lineHeight: od.bpBodyLineHeight }}>
                    •
                </Text>
                <Text
                    style={{
                        flex: 1,
                        fontSize: od.bpBodySize,
                        fontFamily: PF.sans,
                        color: c(od.bpBodyColor),
                        lineHeight: od.bpBodyLineHeight,
                    }}
                >
                    {b}
                </Text>
            </View>
        ))}
    </View>
);

// Render basic-details key/value pairs as explicit 2-column rows instead of a
// flex-wrapped pill grid. The previous `flexWrap: 'wrap'` layout was the root
// cause of the "basic details overlapping the cards below" bug: when pills
// wrapped to 2+ rows, @react-pdf/renderer's yoga layout mis-computed the
// container's height in some flows, so the next sibling (Risk Grade card)
// rendered on top of the bottom pills. Skill rule 2 forbids flexWrap for
// grids exactly because of this — replace with explicit row chunks of 2
// flex:1 cells. Each row has a deterministic height and the container's
// total height is the sum of row heights, which yoga measures correctly.
const PdfBasicDetailItem: React.FC<{ it: OverviewVM['basicDetails']['items'][number] }> = ({ it }) => (
    // `alignItems: 'flex-end'` (not 'center') so the small uppercase label
    // and the larger mono value share a visual bottom rather than visual
    // centers — same pattern used by PdfSectorMetric for value+unit rows.
    // react-pdf's center alignment with mixed font sizes leaves the smaller
    // text floating high relative to the larger one (visible as a baseline
    // mismatch in the printed PDF). Both Texts already carry `lineHeight: 1`
    // (skill rule 9 corollary).
    <View
        style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: 4,
            paddingHorizontal: 12,
            paddingTop: 6,
            paddingBottom: 7,
            borderRadius: 999,
            borderWidth: 0.5,
            borderColor: overviewColors.border,
            backgroundColor: '#FFFFFF',
        }}
    >
        <Text
            style={{
                fontSize: od.bdLabelSize,
                fontFamily: PF.sans,
                fontWeight: 500,
                color: c(od.bdLabelColor),
                letterSpacing: od.bdLabelTrackingEm * od.bdLabelSize,
                textTransform: 'uppercase',
                lineHeight: 1,
            }}
        >
            {it.label}:
        </Text>
        {it.href ? (
            <Link src={it.href}>
                <Text
                    style={{
                        fontSize: od.bdValueSize,
                        fontFamily: PF.mono,
                        fontWeight: 500,
                        color: overviewColors.accent,
                        textDecoration: 'none',
                        lineHeight: 1,
                    }}
                >
                    {it.value}
                </Text>
            </Link>
        ) : (
            <Text
                style={{
                    fontSize: od.bdValueSize,
                    fontFamily: PF.mono,
                    fontWeight: 500,
                    color: c(od.bdValueColor),
                    lineHeight: 1,
                }}
            >
                {it.value}
            </Text>
        )}
    </View>
);

const PdfBasicDetails: React.FC<{ vm: OverviewVM['basicDetails'] }> = ({ vm }) => {
    // Pair items into 2-column rows. Skill rule 2: explicit rows, never
    // flexWrap. The outer card is allowed to wrap to a new page if needed
    // because each row is a small atomic unit (wrap={false} per row).
    const rows: Array<[(typeof vm.items)[number] | undefined, (typeof vm.items)[number] | undefined]> = [];
    for (let i = 0; i < vm.items.length; i += 2) rows.push([vm.items[i], vm.items[i + 1]]);
    return (
        <View
            style={{
                padding: 10,
                borderRadius: 6,
                borderWidth: 0.5,
                borderColor: overviewColors.border,
                backgroundColor: '#fafaf8',
            }}
        >
            {rows.map((row, ri) => (
                <View key={ri} style={{ flexDirection: 'row', gap: 8, marginTop: ri === 0 ? 0 : 6 }} wrap={false}>
                    {row[0] ? <PdfBasicDetailItem it={row[0]} /> : <View style={{ flex: 1 }} />}
                    {row[1] ? <PdfBasicDetailItem it={row[1]} /> : <View style={{ flex: 1 }} />}
                </View>
            ))}
            {vm.address ? (
                // Address row spans both columns (no flex pairing) because the
                // text is long; let it use the full card width and wrap.
                <View style={{ marginTop: 8 }} wrap={false}>
                    <View
                        style={{
                            paddingHorizontal: 12,
                            paddingTop: 6,
                            paddingBottom: 7,
                            borderRadius: 999,
                            borderWidth: 0.5,
                            borderColor: overviewColors.border,
                            backgroundColor: '#FFFFFF',
                        }}
                    >
                        <Text
                            style={{
                                fontSize: od.bdAddressSize,
                                fontFamily: PF.sans,
                                color: c(od.bdAddressColor),
                                lineHeight: od.bdAddressLineHeight,
                            }}
                        >
                            {vm.address}
                        </Text>
                    </View>
                </View>
            ) : null}
        </View>
    );
};

const PdfNewsCard: React.FC<{ item: OverviewVM['news'][number] }> = ({ item }) => {
    const sentColors = {
        pos: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        neg: { bg: overviewColors.negSoft, fg: overviewColors.neg },
        neutral: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    }[item.sentiment];
    return (
        // No `flex: 1` on the card — the parent column wrapper (`<View flex:1>`)
        // already owns the column width. Doubling flex on the card was causing
        // intermittent zero-width renders in @react-pdf/renderer.
        <View
            style={{
                backgroundColor: '#FFFFFF',
                borderWidth: nd.cardBorder,
                borderColor: overviewColors.border,
                borderRadius: nd.cardRadius,
                padding: nd.cardPad,
            }}
            wrap={false}
        >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: nd.sourceGap, flex: 1 }}>
                    <View
                        style={{
                            width: nd.sourcePillSize,
                            height: nd.sourcePillSize,
                            borderRadius: nd.sourcePillRadius,
                            backgroundColor: c(nd.sourcePillBg),
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Text
                            style={{
                                fontSize: nd.sourcePillTextSize,
                                color: c(nd.sourcePillTextColor),
                                fontFamily: PF.sans,
                                fontWeight: 600,
                                // 1.05 (not 1.0) — at lineHeight 1, react-pdf occasionally
                                // pushes 1-char initials below the pill's vertical center.
                                lineHeight: 1.05,
                            }}
                        >
                            {item.initials}
                        </Text>
                    </View>
                    {/* Wrap the source name in a flex:1 View, not a flex:1 Text.
                        `flex: 1` directly on Text in react-pdf can let an unwrapped
                        long string spill outside the card and overlap the headline
                        below — visible as a stray character behind the body text. */}
                    <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: nd.sourceLabelSize, color: c(nd.sourceLabelColor), fontFamily: PF.sans, fontWeight: 600 }}>
                            {item.source}
                        </Text>
                    </View>
                </View>
                {/* Sentiment chip. Centering text inside a chip in @react-pdf/renderer
                    is a known trap: the text baseline lands toward the bottom of the
                    line box, so a chip with symmetric padding looks "text too low".
                    The fix is THREE coordinated things — change one without the
                    others and the text drifts again:
                      1. `lineHeight: 1` on the Text — collapses the line box so it
                         hugs the glyph, removing the extra ascender space above.
                      2. `alignItems: 'center', justifyContent: 'center'` on the View
                         — tells the View to center the (now glyph-sized) Text box.
                      3. Asymmetric padding (top < bottom by ~1pt) — counteracts the
                         residual baseline offset that lineHeight:1 doesn't fully
                         remove. paddingTop:2 / paddingBottom:3 looks centered. */}
                <View
                    style={{
                        backgroundColor: sentColors.bg,
                        paddingHorizontal: nd.sentChipPadX,
                        paddingTop: 2,
                        paddingBottom: 3,
                        borderRadius: nd.sentChipRadius,
                        flexShrink: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: nd.sentChipSize,
                            fontFamily: PF.sans,
                            fontWeight: 600,
                            color: sentColors.fg,
                            letterSpacing: nd.sentChipTrackingEm * nd.sentChipSize,
                            textTransform: 'uppercase',
                            lineHeight: 1,
                        }}
                    >
                        {item.sentimentLabel}
                    </Text>
                </View>
            </View>
            {/* Reserve 2 headline lines so the 2x2 grid equalizes regardless of
                whether a given headline wraps to one or two lines. Wrap text in a
                View because marginTop on a wrapping Text can collapse in react-pdf.
                When a redirectUrl is available the headline is wrapped in
                <Link> — the PDF reader treats it as a clickable hyperlink to
                the original article. Brand colour + underline so readers can
                see at a glance that the headline is clickable. */}
            <View style={{ marginTop: 6, minHeight: nd.headlineSize * nd.headlineLineHeight * 2 }}>
                {item.url ? (
                    <Link
                        src={item.url}
                        style={{
                            fontSize: nd.headlineSize,
                            fontFamily: PF.sans,
                            fontWeight: 500,
                            color: '#6021B3',
                            lineHeight: nd.headlineLineHeight,
                            textDecoration: 'underline',
                        }}
                    >
                        {item.headline}
                    </Link>
                ) : (
                    <Text
                        style={{
                            fontSize: nd.headlineSize,
                            fontFamily: PF.sans,
                            fontWeight: 500,
                            color: c(nd.headlineColor),
                            lineHeight: nd.headlineLineHeight,
                        }}
                    >
                        {item.headline}
                    </Text>
                )}
            </View>
            <View style={{ marginTop: 6 }}>
                <Text style={{ fontSize: nd.dateSize, fontFamily: PF.mono, color: c(nd.dateColor) }}>{item.date}</Text>
            </View>
        </View>
    );
};

const PdfSectorMetric: React.FC<{ m: SectorMetric }> = ({ m }) => (
    <View
        style={{
            flex: 1,
            backgroundColor: '#FFFFFF',
            borderWidth: nd.cardBorder,
            borderColor: overviewColors.border,
            borderRadius: nd.cardRadius,
            paddingHorizontal: nd.sectorMetricPad,
            paddingVertical: 6,
            minHeight: 60,
        }}
        wrap={false}
    >
        <Text
            style={{
                fontSize: nd.sectorMetricLabelSize,
                fontFamily: PF.sans,
                fontWeight: 600,
                color: c(nd.sectorMetricLabelColor),
                letterSpacing: nd.sectorMetricLabelTrackingEm * nd.sectorMetricLabelSize,
                textTransform: 'uppercase',
            }}
        >
            {m.label}
        </Text>
        {/* Skill rule 9 corollary: value (large mono) + unit (small mono) on
            `alignItems: 'baseline'` drifts the unit downward in react-pdf.
            Switch to 'flex-end' + lineHeight: 1 so the optical bottoms align
            instead of the unreliable baselines. */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2, marginTop: 3 }}>
            <Text
                style={{
                    fontSize: nd.sectorMetricValueSize,
                    fontFamily: PF.mono,
                    fontWeight: 500,
                    color: m.valueColor ?? c(nd.sectorMetricValueColor),
                    lineHeight: 1,
                }}
            >
                {m.value}
            </Text>
            {m.unit ? (
                <Text
                    style={{
                        fontSize: nd.sectorMetricUnitSize,
                        fontFamily: PF.mono,
                        color: c(nd.sectorMetricUnitColor),
                        lineHeight: 1,
                    }}
                >
                    {m.unit}
                </Text>
            ) : null}
        </View>
        {/* Sub text ("FY24, convenience foods", "by 2028", etc.). The card is a
            flex column with `minHeight: 60`; the flex:1 spacer above pushes the
            sub to the bottom of that reserved space, and `textAlign: 'right'`
            anchors it to the right edge — quieter than sitting flush under the
            value. Smaller font (sectorMetricSubSize - 1pt) reduces visual weight. */}
        {m.sub ? (
            <>
                <View style={{ flex: 1 }} />
                <Text
                    style={{
                        fontSize: nd.sectorMetricSubSize - 1,
                        fontFamily: PF.sans,
                        color: c(nd.sectorMetricSubColor),
                        textAlign: 'right',
                        marginTop: 4,
                    }}
                >
                    {m.sub}
                </Text>
            </>
        ) : null}
    </View>
);

const PdfDriverList: React.FC<{ items: string[]; header: string; tone: 'green' | 'red' }> = ({ items, header, tone }) => {
    const headerColor = tone === 'green' ? c(nd.drGreenColor) : c(nd.drRedColor);
    return (
        <View
            style={{
                flex: 1,
                backgroundColor: c(nd.drCardBg),
                borderRadius: nd.drCardRadius,
                padding: nd.drCardPad,
            }}
        >
            <Text
                style={{
                    fontSize: nd.drHeaderSize,
                    fontFamily: PF.sans,
                    fontWeight: 500,
                    color: headerColor,
                    letterSpacing: nd.drHeaderTrackingEm * nd.drHeaderSize,
                    textTransform: 'uppercase',
                    marginBottom: 5,
                }}
            >
                {header}
            </Text>
            {items.map((item, i) => (
                <View
                    key={i}
                    style={{
                        flexDirection: 'row',
                        gap: 7,
                        paddingVertical: nd.drListItemPadY,
                        borderBottomWidth: i < items.length - 1 ? nd.drListBorder : 0,
                        borderBottomColor: c(nd.drListBorderColor),
                    }}
                >
                    <Text style={{ fontSize: nd.drDashSize, fontFamily: PF.display, color: c(nd.drDashColor), lineHeight: 1 }}>—</Text>
                    <Text style={{ flex: 1, fontSize: nd.drItemSize, fontFamily: PF.sans, color: c(nd.drItemColor), lineHeight: 1.5 }}>
                        {item}
                    </Text>
                </View>
            ))}
        </View>
    );
};

// ── Key Competitors: 2×2 cards layout (Figma 131:85949) ─────────
//
// Two columns, two rows. Subject company sits in the top-left with a teal
// left border and a tinted background; competitors are plain white cards.
// Each card: name (capped at 2 lines via minHeight) + kicker, description
// (capped at 4 lines via minHeight per skill rule 3), divider, then a
// "Founded" + "Location" footer row.
//
// Why 2×2 instead of 4-up: a single row of 4 cards across A4 gives each
// card ~115pt of width — name + description for entities like "Arya.ag
// (Arya Collateral Warehousing Services Private Limited)" overflow that
// budget badly. 2 columns gives each card ~245pt — comfortable for typical
// entity names without ballooning vertically.
//
// Width is driven by `flex: 1` siblings — never `flexWrap` (skill rule 2).

const pairsOfCards = <T,>(arr: T[]): Array<[T | undefined, T | undefined]> => {
    const out: Array<[T | undefined, T | undefined]> = [];
    for (let i = 0; i < arr.length; i += 2) out.push([arr[i], arr[i + 1]]);
    return out;
};

const PdfCompetitorsCards: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cards = vm.cards;
    if (cards.length === 0) return null;
    const nameMinHeight = cd.cardNameSize * cd.cardNameLineHeight * cd.cardNameMaxLines;
    const descMinHeight = cd.cardDescSize * cd.cardDescLineHeight * cd.cardDescMaxLines;
    const rows = pairsOfCards(cards);
    return (
        <View style={{ flexDirection: 'column', gap: cd.cardGap }}>
            {rows.map((row, ri) => (
                <View key={ri} style={{ flexDirection: 'row', gap: cd.cardGap }} wrap={false}>
                    {row.map((card, ci) => {
                        if (!card) return <View key={ci} style={{ flex: 1 }} />;
                        const subject = card.kind === 'subject';
                        // Tightened paddings — earlier the card had cardPad on
                        // all four sides plus an explicit kicker line for both
                        // subject and competitor rows. The competitor kicker
                        // "Competitor" was redundant noise (the card layout +
                        // 2-up grid already conveys the row meaning). Dropping
                        // it lets the description start higher; combined with
                        // the trimmed vertical padding it removes most of the
                        // dead space the user flagged.
                        const padX = cd.cardPad - 1.5;
                        const padY = cd.cardPad - 2.5;
                        return (
                            <View
                                key={ci}
                                wrap={false}
                                style={{
                                    flex: 1,
                                    backgroundColor: subject ? c(cd.cardSubjectBg) : '#FFFFFF',
                                    borderTopWidth: cd.cardBorder,
                                    borderRightWidth: cd.cardBorder,
                                    borderBottomWidth: cd.cardBorder,
                                    borderLeftWidth: subject ? cd.cardSubjectBorderLeft : cd.cardBorder,
                                    borderTopColor: c(cd.cardBorderColor),
                                    borderRightColor: c(cd.cardBorderColor),
                                    borderBottomColor: c(cd.cardBorderColor),
                                    borderLeftColor: subject ? c(cd.cardSubjectBorderColor) : c(cd.cardBorderColor),
                                    borderRadius: cd.cardRadius,
                                    paddingTop: padY,
                                    paddingBottom: padY,
                                    paddingRight: padX,
                                    paddingLeft: subject ? padX + cd.cardSubjectBorderLeft - cd.cardBorder : padX,
                                }}
                            >
                                <View style={{ minHeight: nameMinHeight }}>
                                    <Text
                                        style={{
                                            fontSize: cd.cardNameSize,
                                            fontFamily: PF.sans,
                                            fontWeight: 600,
                                            color: c(cd.cardNameColor),
                                            lineHeight: cd.cardNameLineHeight,
                                        }}
                                    >
                                        {card.name}
                                    </Text>
                                </View>
                                {subject ? (
                                    <Text
                                        style={{
                                            fontSize: cd.cardKickerSize,
                                            fontFamily: PF.sans,
                                            fontWeight: 500,
                                            color: c(cd.cardKickerSubjectColor),
                                            letterSpacing: cd.cardKickerTrackingEm * cd.cardKickerSize,
                                            textTransform: 'uppercase',
                                            marginTop: cd.cardKickerGap,
                                        }}
                                    >
                                        Subject Company
                                    </Text>
                                ) : null}
                                <View style={{ marginTop: subject ? cd.cardDescGap : cd.cardKickerGap, minHeight: descMinHeight }}>
                                    {card.description ? (
                                        <Text
                                            style={{
                                                fontSize: cd.cardDescSize,
                                                fontFamily: PF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: cd.cardDescLineHeight,
                                            }}
                                        >
                                            {card.description}
                                        </Text>
                                    ) : null}
                                    {card.truncated ? (
                                        // No `fontStyle: 'italic'` here — Geist
                                        // is only registered in regular + medium
                                        // + bold variants (see pdfFonts.ts), so
                                        // an italic style crashes the PDF with
                                        // "Could not resolve font for Geist,
                                        // fontWeight 400, fontStyle italic".
                                        // Use letterSpacing + muted color + a
                                        // leading "↳" as the visual cue instead.
                                        <Text
                                            style={{
                                                fontSize: cd.cardDescSize - 0.5,
                                                fontFamily: PF.sans,
                                                color: overviewColors.inkMuted,
                                                letterSpacing: 0.2,
                                                marginTop: 3,
                                            }}
                                        >
                                            ↳ Truncated — see "About the Company" above for the full text.
                                        </Text>
                                    ) : null}
                                </View>
                                <View
                                    style={{
                                        borderTopWidth: cd.cardBorder,
                                        borderTopColor: cd.cardDividerColor,
                                        marginTop: 6,
                                    }}
                                >
                                    {(
                                        [
                                            { label: 'Founded', value: card.foundingYear },
                                            { label: 'Location', value: card.location },
                                            { label: 'Website', value: card.website, href: card.website },
                                        ] as Array<{ label: string; value?: string; href?: string }>
                                    )
                                        .filter((r) => !!r.value)
                                        .map((r, idx, arr) => (
                                            <View
                                                key={r.label}
                                                style={{
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-between',
                                                    paddingTop: cd.cardRowPadY,
                                                    paddingBottom: cd.cardRowPadY,
                                                    borderBottomWidth: idx === arr.length - 1 ? 0 : cd.cardBorder,
                                                    borderBottomColor: cd.cardDividerColor,
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        fontSize: cd.cardRowSize,
                                                        fontFamily: PF.sans,
                                                        color: c(cd.cardRowLabelColor),
                                                    }}
                                                >
                                                    {r.label}
                                                </Text>
                                                {r.href ? (
                                                    <Link src={r.href}>
                                                        <Text
                                                            style={{
                                                                fontSize: cd.cardRowSize,
                                                                fontFamily: PF.mono,
                                                                fontWeight: 500,
                                                                color: overviewColors.accent,
                                                                textDecoration: 'none',
                                                            }}
                                                        >
                                                            {(r.value ?? '').replace(/^https?:\/\//i, '').replace(/\/$/, '')}
                                                        </Text>
                                                    </Link>
                                                ) : (
                                                    <Text
                                                        style={{
                                                            fontSize: cd.cardRowSize,
                                                            fontFamily: PF.mono,
                                                            fontWeight: 500,
                                                            color: c(cd.cardRowValueColor),
                                                        }}
                                                    >
                                                        {r.value}
                                                    </Text>
                                                )}
                                            </View>
                                        ))}
                                </View>
                                {card.keyAchievements ? (
                                    <View style={{ marginTop: 6 }}>
                                        <Text
                                            style={{
                                                fontSize: cd.cardKickerSize,
                                                fontFamily: PF.sans,
                                                fontWeight: 600,
                                                color: c(cd.cardRowLabelColor),
                                                letterSpacing: cd.cardKickerTrackingEm * cd.cardKickerSize,
                                                textTransform: 'uppercase',
                                                marginBottom: 3,
                                            }}
                                        >
                                            Key Achievements
                                        </Text>
                                        <Text
                                            style={{
                                                fontSize: cd.cardDescSize,
                                                fontFamily: PF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: cd.cardDescLineHeight,
                                            }}
                                        >
                                            {card.keyAchievements}
                                        </Text>
                                    </View>
                                ) : null}
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
};

// Legacy comparison table — kept in source so we can swap back via the
// render line below if the new card layout doesn't fit a future spec.
const PdfCompetitorsTable: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cols = [vm.focal.name, ...vm.rivals.map((r) => r.name)];
    return (
        <View
            style={{
                borderTopWidth: cd.headerBorder,
                borderTopColor: c(cd.headerBorderColor),
                borderBottomWidth: cd.headerBorder,
                borderBottomColor: c(cd.headerBorderColor),
            }}
        >
            {/* Header row */}
            <View style={{ flexDirection: 'row' }} wrap={false}>
                <View style={{ width: cd.labelColWidth }} />
                {cols.map((name, i) => {
                    const focal = i === 0;
                    return (
                        <View
                            key={i}
                            style={{
                                flex: 1,
                                backgroundColor: focal ? c(cd.focalHeaderBg) : '#FFFFFF',
                                borderBottomWidth: cd.headerBorder,
                                borderBottomColor: focal ? c(cd.focalHeaderBorder) : c(cd.headerBorderColor),
                                paddingHorizontal: cd.cellPadX,
                                paddingVertical: cd.cellPadY + 4,
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: cd.headerSize,
                                    fontFamily: PF.sans,
                                    fontWeight: 600,
                                    color: focal ? c(cd.headerFocalColor) : c(cd.headerColor),
                                    letterSpacing: cd.headerTrackingEm * cd.headerSize,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {name}
                            </Text>
                        </View>
                    );
                })}
            </View>
            {/* Body rows */}
            {vm.rows.map((row) => (
                <View key={row.key} style={{ flexDirection: 'row' }} wrap={false}>
                    <View
                        style={{
                            width: cd.labelColWidth,
                            paddingHorizontal: cd.cellPadX,
                            paddingVertical: cd.cellPadY,
                            borderBottomWidth: cd.border,
                            borderBottomColor: c(cd.borderColor),
                        }}
                    >
                        <Text
                            style={{
                                fontSize: cd.labelSize,
                                fontFamily: PF.sans,
                                fontWeight: 500,
                                color: c(cd.labelColor),
                                letterSpacing: cd.labelTrackingEm * cd.labelSize,
                                textTransform: 'uppercase',
                            }}
                        >
                            {row.label}
                        </Text>
                    </View>
                    {row.values.map((value, i) => {
                        const focal = i === 0;
                        const muted = value === 'Not disclosed';
                        return (
                            <View
                                key={i}
                                style={{
                                    flex: 1,
                                    backgroundColor: focal ? c(cd.focalBg) : '#FFFFFF',
                                    paddingHorizontal: cd.cellPadX,
                                    paddingVertical: cd.cellPadY,
                                    borderBottomWidth: cd.border,
                                    borderBottomColor: c(cd.borderColor),
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: cd.cellSize,
                                        fontFamily: row.mono ? PF.mono : PF.sans,
                                        color: muted ? c(cd.cellMutedColor) : c(cd.cellColor),
                                        lineHeight: cd.cellLineHeight,
                                    }}
                                >
                                    {value}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
};

const PdfOverview: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    // Intentionally no `if (!data.overview) return null` — buildVM uses optional
    // chaining throughout, so missing `overview` payload is fine. We want the
    // section to render whenever ANY sub-block (news, competitors, sector,
    // risk grade, charts) has data, even if the About / Business Profile
    // narrative is absent. The per-block ternaries below handle their own
    // emptiness — the `hasData` gate above already ensures we have something
    // meaningful to show.
    const vm = buildVM(data);

    return (
        <PdfSection kicker="Company" title="Company Overview" index={index} breakBefore={breakBefore}>
            <PdfIdentityCard vm={vm} />

            {vm.aboutBullets && vm.aboutBullets.length > 0 ? (
                // Wrap in a View with minPresenceAhead so the "About the
                // Company" heading doesn't orphan at the bottom of a page
                // when bullets push to the next. Bullets themselves are
                // allowed to flow naturally — long paragraphs may span pages.
                <View minPresenceAhead={60}>
                    <PdfH3>About the Company</PdfH3>
                    {vm.aboutBullets.map((b, i) => (
                        <View key={i} style={{ flexDirection: 'row', gap: 5, marginTop: i > 0 ? 3 : 0 }}>
                            <Text
                                style={{
                                    fontSize: od.bodySize,
                                    fontFamily: PF.sans,
                                    color: c(od.bodyColor),
                                    lineHeight: od.bodyLineHeight,
                                }}
                            >
                                •
                            </Text>
                            <Text
                                style={{
                                    flex: 1,
                                    fontSize: od.bodySize,
                                    fontFamily: PF.sans,
                                    color: c(od.bodyColor),
                                    lineHeight: od.bodyLineHeight,
                                }}
                            >
                                {b}
                            </Text>
                        </View>
                    ))}
                </View>
            ) : null}

            {vm.businessCards.length > 0 ? (
                // minPresenceAhead 50: enough to keep the heading from
                // orphaning. The 3-up card row itself is wrap={false} so
                // it stays atomic (~150pt); if 150pt doesn't fit, the
                // wrap=false constraint pushes the row but the heading
                // sits with it via minPresenceAhead. Was 140 — too eager
                // to push the whole block to a new page and waste tail
                // whitespace on the page above.
                <View minPresenceAhead={50}>
                    <PdfH3>Business Profile</PdfH3>
                    <View style={{ flexDirection: 'row', gap: od.bpGap }} wrap={false}>
                        {vm.businessCards.map((card) => (
                            <PdfBusinessCard key={card.title} title={card.title} bullets={card.bullets} />
                        ))}
                    </View>
                </View>
            ) : null}

            {vm.basicDetails.items.length > 0 ? (
                // minPresenceAhead 40: just enough to keep the heading
                // glued to the first KV row. The card flows naturally below.
                <View style={{ marginTop: interSectionGap }} minPresenceAhead={40}>
                    <PdfH3 topGap={0}>Basic Details</PdfH3>
                    <PdfBasicDetails vm={vm.basicDetails} />
                </View>
            ) : null}

            {vm.news.length > 0 ? (
                // minPresenceAhead 50: reserves just heading + the start of
                // the first row pair. The previous 140 was too aggressive —
                // when Basic Details left only ~100pt of trailing space on
                // its page, News couldn't satisfy 140 and got pushed to a
                // fresh page, leaving ~400pt of trailing whitespace behind.
                // With 50, the heading and first ~30pt of content can render
                // wherever there's space; remaining card rows flow naturally.
                // Each row pair is still wrap={false} so a single row never
                // splits, and news items are independent enough that a row
                // landing on the next page reads fine.
                <View style={{ marginTop: interSectionGap }} minPresenceAhead={50}>
                    <PdfH3 topGap={0}>News &amp; Updates</PdfH3>
                    {/* 2x2 grid built as explicit rows of 2. flex-wrap was unreliable in
                        react-pdf — heights diverged and cards overlapped. Explicit rows
                        with flex:1 children give a stable 2-up layout. */}
                    {[
                        [vm.news[0], vm.news[1]],
                        [vm.news[2], vm.news[3]],
                    ]
                        .filter((row) => row[0] || row[1])
                        .map((row, ri) => (
                            <View
                                key={ri}
                                style={{ flexDirection: 'row', gap: nd.gridGap, marginTop: ri === 0 ? 0 : nd.gridGap }}
                                wrap={false}
                            >
                                {row.map((n, ci) =>
                                    n ? (
                                        <View key={ci} style={{ flex: 1 }} wrap={false}>
                                            <PdfNewsCard item={n} />
                                        </View>
                                    ) : (
                                        <View key={ci} style={{ flex: 1 }} />
                                    ),
                                )}
                            </View>
                        ))}
                </View>
            ) : null}

            {vm.sectorOutlook ? (
                // minPresenceAhead bumped 60 → 140: 60pt only kept the heading
                // line glued to the next page, leaving the metric-cards row
                // free to orphan or split. 140pt reserves heading + the metric
                // row (~80pt) together. The body text and Growth/Risks
                // section below are still allowed to flow naturally — they're
                // text-heavy and benefit from natural wrap.
                <View style={{ marginTop: interSectionGap }} minPresenceAhead={140}>
                    {/* Skill rule 9 corollary: heading + caption mix sizes/
                        weights — `alignItems: 'baseline'` drifts the caption.
                        Switch to 'center' + lineHeight: 1 on both Texts. */}
                    <View
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: od.headingToBody,
                        }}
                    >
                        <Text style={{ fontSize: od.h3Size, fontFamily: PF.sans, fontWeight: 600, color: c(od.h3Color), lineHeight: 1 }}>
                            Sector / Industry Outlook
                        </Text>
                        {vm.sectorOutlook.sectorName ? (
                            <Text style={{ fontSize: 7.5, fontFamily: PF.sans, color: overviewColors.inkMuted, lineHeight: 1 }}>
                                {vm.sectorOutlook.sectorName}
                            </Text>
                        ) : null}
                    </View>
                    {vm.sectorOutlook.metrics.length > 0 ? (
                        // wrap={false} on the metric-card row so the row never
                        // splits across pages — a half-row of metric cards
                        // with the rest dangling at the top of the next page
                        // reads as a render glitch.
                        <View style={{ flexDirection: 'row', gap: nd.gridGap, marginBottom: nd.sectorGap }} wrap={false}>
                            {vm.sectorOutlook.metrics.map((m) => (
                                <PdfSectorMetric key={m.label} m={m} />
                            ))}
                        </View>
                    ) : null}
                    {vm.sectorOutlook.body ? (
                        <Text
                            style={{
                                fontSize: nd.sectorBodySize,
                                fontFamily: PF.sans,
                                color: c(nd.sectorBodyColor),
                                lineHeight: nd.sectorBodyLineHeight,
                                marginBottom: nd.sectorGap,
                            }}
                        >
                            {vm.sectorOutlook.body}
                        </Text>
                    ) : null}
                    {vm.sectorOutlook.growthDrivers.length > 0 || vm.sectorOutlook.keyRisks.length > 0 ? (
                        // `break` (react-pdf's force-page-break) starts Growth
                        // Drivers + Key Risks on a fresh page — the lists are
                        // visually dense and reading them mid-page below the
                        // long sector body paragraph hurt scannability. The
                        // small empty tail on the Sector body page is the
                        // intentional trade.
                        <View break style={{ flexDirection: 'row', gap: nd.gridGap }}>
                            {vm.sectorOutlook.growthDrivers.length > 0 ? (
                                <PdfDriverList items={vm.sectorOutlook.growthDrivers} header="Growth Drivers" tone="green" />
                            ) : null}
                            {vm.sectorOutlook.keyRisks.length > 0 ? (
                                <PdfDriverList items={vm.sectorOutlook.keyRisks} header="Key Risks" tone="red" />
                            ) : null}
                        </View>
                    ) : null}
                </View>
            ) : null}

            {vm.competitors ? (
                // Previously `wrap={false}` on the whole block — that solved
                // the heading-orphan but forced the entire ~295pt grid to
                // jump to a new page when it didn't fit, leaving large empty
                // tracts at the bottom of the previous page.
                //
                // minPresenceAhead 50: enough to keep the heading + start of
                // the first competitor card glued. Each card row inside
                // PdfCompetitorsCards is wrap={false}, so cards never split
                // mid-card. The trade is small (heading sometimes lands
                // near a page boundary) for much better page packing.
                <View style={{ marginTop: interSectionGap }} minPresenceAhead={50}>
                    <PdfH3 topGap={0}>Key Competitors</PdfH3>
                    <PdfCompetitorsCards vm={vm.competitors} />
                </View>
            ) : null}
            <PdfOverviewMiniCharts data={data} />
        </PdfSection>
    );
};

// Employee headcount chart. Website Traffic was previously stacked below this
// but has been removed (signal was noisy and the SimilarWeb data wasn't load-
// bearing for the credit narrative).
const buildEmployeePoints = (data: ReportData): LineChartPoint[] => {
    const mom = data.management?.momEmployee ?? [];
    return mom.slice(-18).map((x) => ({ label: fmtMonthYearShort(x.yearMonth), value: x.count }));
};
const PdfOverviewMiniCharts: React.FC<{ data: ReportData }> = ({ data }) => {
    // Body width comes from PdfBodyWidthProvider (provided by PdfPage). On A4
    // with default paddings this resolves to 520pt — exactly the historical
    // CHART_FULL_WIDTH the chart was using before this context existed — so
    // un-overridden output is byte-identical. On A3 / reduced paddings the
    // chart grows to fill the new usable body width.
    const emp = buildEmployeePoints(data);
    const chartWidth = usePdfBodyWidth();
    if (emp.length < 2) return null;
    // minPresenceAhead 50: just enough to keep the heading from orphaning.
    // The inner View wrap=false already keeps heading + chart atomic, so
    // the lookahead just needs to ensure the inner block has room to start.
    return (
        <View style={{ marginTop: interSectionGap }} minPresenceAhead={50}>
            <View wrap={false}>
                <PdfH3 topGap={0}>Employee Headcount</PdfH3>
                <PdfLineChart points={emp} height={120} width={chartWidth} />
                <Text style={{ fontSize: 6.5, color: overviewColors.inkMuted, marginTop: 4 }}>
                    Monthly EPF-registered employees · last 18 months
                </Text>
            </View>
        </View>
    );
};

// ════════════════════════════════════════════════════════════════
//                          S C R E E N
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;

const ScreenH3: React.FC<{ children: React.ReactNode; topGap?: number }> = ({ children, topGap = od.sectionGap }) => (
    <div
        style={{
            fontSize: ptToPx(od.h3Size),
            fontFamily: SF.sans,
            fontWeight: 600,
            color: c(od.h3Color),
            marginTop: ptToPx(topGap),
            marginBottom: ptToPx(od.headingToBody),
        }}
    >
        {children}
    </div>
);

const ScreenChipBox: React.FC<{ label: string; tone: ChipTone }> = ({ label, tone }) => {
    const tones: Record<ChipTone, { bg: string; fg: string }> = {
        positive: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        accent: { bg: overviewColors.accentSoft, fg: overviewColors.accentDeep },
        muted: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    };
    const t = tones[tone];
    return (
        <span
            style={{
                background: t.bg,
                paddingLeft: ptToPx(od.idChipPadX),
                paddingRight: ptToPx(od.idChipPadX),
                paddingTop: ptToPx(od.idChipPadY),
                paddingBottom: ptToPx(od.idChipPadY + 1),
                borderRadius: ptToPx(od.idChipRadius),
                fontSize: ptToPx(od.idChipSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: t.fg,
                letterSpacing: `${od.idChipTrackingEm}em`,
                textTransform: 'uppercase',
            }}
        >
            {label}
        </span>
    );
};

const ScreenIdentityCard: React.FC<{ vm: OverviewVM }> = ({ vm }) => (
    <div
        style={{
            background: overviewColors.surface,
            borderRadius: ptToPx(od.idCardRadius),
            paddingLeft: ptToPx(od.idCardPadX),
            paddingRight: ptToPx(od.idCardPadX),
            paddingTop: ptToPx(od.idCardPadY),
            paddingBottom: ptToPx(od.idCardPadY),
            display: 'flex',
            alignItems: 'center',
            gap: ptToPx(od.idCardGap),
        }}
    >
        <div
            style={{
                width: ptToPx(od.idLogoSize),
                height: ptToPx(od.idLogoSize),
                borderRadius: ptToPx(od.idLogoRadius),
                background: overviewColors.ink,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <span style={{ fontSize: ptToPx(od.idLogoTextSize), color: '#FFFFFF', fontFamily: SF.display, lineHeight: 1 }}>
                {vm.initial}
            </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(od.idChipGap), flexWrap: 'wrap' }}>
                <span
                    style={{
                        fontSize: ptToPx(od.idTitleSize),
                        fontFamily: SF.display,
                        color: overviewColors.ink,
                        lineHeight: od.idTitleLineHeight,
                    }}
                >
                    {vm.orgTitle}
                </span>
                {vm.chips.map((ch) => (
                    <ScreenChipBox key={ch.label} label={ch.label} tone={ch.tone} />
                ))}
            </div>
            {vm.subtitle ? (
                <div
                    style={{
                        fontSize: ptToPx(od.idSubtitleSize),
                        fontFamily: SF.sans,
                        color: c(od.idSubtitleColor),
                        marginTop: 4,
                        lineHeight: od.idSubtitleLineHeight,
                    }}
                >
                    {vm.subtitle}
                </div>
            ) : null}
        </div>
    </div>
);

const ScreenBusinessCard: React.FC<{ title: string; bullets: string[] }> = ({ title, bullets }) => (
    <div
        style={{
            flex: 1,
            background: '#fafaf8',
            border: `${ptToPx(od.bpCardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(od.bpCardRadius),
            padding: ptToPx(od.bpCardPad),
        }}
    >
        <div
            style={{
                fontSize: ptToPx(od.bpTitleSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: c(od.bpTitleColor),
                marginBottom: 6,
            }}
        >
            {title}
        </div>
        {bullets.map((b, i) => (
            <div
                key={i}
                style={{
                    display: 'flex',
                    gap: 5,
                    marginTop: i > 0 ? 4 : 0,
                    fontSize: ptToPx(od.bpBodySize),
                    fontFamily: SF.sans,
                    color: c(od.bpBodyColor),
                    lineHeight: od.bpBodyLineHeight,
                }}
            >
                <span>•</span>
                <span style={{ flex: 1 }}>{b}</span>
            </div>
        ))}
    </div>
);

const ScreenBasicDetails: React.FC<{ vm: OverviewVM['basicDetails'] }> = ({ vm }) => (
    <div
        style={{
            padding: 12,
            borderRadius: 8,
            border: `0.5px solid ${overviewColors.border}`,
            background: '#fafaf8',
        }}
    >
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 10, rowGap: 8 }}>
            {vm.items.map((it) => (
                <div
                    key={it.label}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '7px 14px 8px',
                        borderRadius: 999,
                        border: `0.5px solid ${overviewColors.border}`,
                        background: '#FFFFFF',
                    }}
                >
                    <span
                        style={{
                            fontSize: ptToPx(od.bdLabelSize),
                            fontFamily: SF.sans,
                            fontWeight: 500,
                            color: c(od.bdLabelColor),
                            letterSpacing: `${od.bdLabelTrackingEm}em`,
                            textTransform: 'uppercase',
                            lineHeight: 1,
                        }}
                    >
                        {it.label}:
                    </span>
                    {it.href ? (
                        <a
                            href={it.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                fontSize: ptToPx(od.bdValueSize),
                                fontFamily: SF.mono,
                                fontWeight: 500,
                                color: overviewColors.accent,
                                textDecoration: 'none',
                                lineHeight: 1,
                            }}
                        >
                            {it.value}
                        </a>
                    ) : (
                        <span
                            style={{
                                fontSize: ptToPx(od.bdValueSize),
                                fontFamily: SF.mono,
                                fontWeight: 500,
                                color: c(od.bdValueColor),
                                lineHeight: 1,
                            }}
                        >
                            {it.value}
                        </span>
                    )}
                </div>
            ))}
        </div>
        {vm.address ? (
            // Inline-block + width:fit-content so the address pill shrinks to
            // its text width instead of stretching to fill the container.
            <div style={{ marginTop: 10, display: 'flex' }}>
                <span
                    style={{
                        padding: '7px 14px 8px',
                        borderRadius: 999,
                        border: `0.5px solid ${overviewColors.border}`,
                        background: '#FFFFFF',
                        fontSize: ptToPx(od.bdAddressSize),
                        fontFamily: SF.sans,
                        color: c(od.bdAddressColor),
                        lineHeight: od.bdAddressLineHeight,
                        display: 'inline-block',
                    }}
                >
                    {vm.address}
                </span>
            </div>
        ) : null}
    </div>
);

const ScreenNewsCard: React.FC<{ item: OverviewVM['news'][number] }> = ({ item }) => {
    const sentColors = {
        pos: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        neg: { bg: overviewColors.negSoft, fg: overviewColors.neg },
        neutral: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    }[item.sentiment];
    return (
        <div
            style={{
                flex: 1,
                background: '#FFFFFF',
                border: `${ptToPx(nd.cardBorder)}px solid ${overviewColors.border}`,
                borderRadius: ptToPx(nd.cardRadius),
                padding: ptToPx(nd.cardPad),
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(nd.sourceGap) }}>
                    <span
                        style={{
                            width: ptToPx(nd.sourcePillSize),
                            height: ptToPx(nd.sourcePillSize),
                            borderRadius: '50%',
                            background: c(nd.sourcePillBg),
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: ptToPx(nd.sourcePillTextSize),
                            color: c(nd.sourcePillTextColor),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                        }}
                    >
                        {item.initials}
                    </span>
                    <span
                        style={{
                            fontSize: ptToPx(nd.sourceLabelSize),
                            color: c(nd.sourceLabelColor),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                        }}
                    >
                        {item.source}
                    </span>
                </div>
                <span
                    style={{
                        background: sentColors.bg,
                        paddingLeft: ptToPx(nd.sentChipPadX),
                        paddingRight: ptToPx(nd.sentChipPadX),
                        paddingTop: ptToPx(nd.sentChipPadY),
                        paddingBottom: ptToPx(nd.sentChipPadY + 1),
                        borderRadius: ptToPx(nd.sentChipRadius),
                        fontSize: ptToPx(nd.sentChipSize),
                        fontFamily: SF.sans,
                        fontWeight: 600,
                        color: sentColors.fg,
                        letterSpacing: `${nd.sentChipTrackingEm}em`,
                        textTransform: 'uppercase',
                    }}
                >
                    {item.sentimentLabel}
                </span>
            </div>
            <div
                style={{
                    marginTop: 5,
                    fontSize: ptToPx(nd.headlineSize),
                    fontFamily: SF.sans,
                    fontWeight: 500,
                    color: c(nd.headlineColor),
                    lineHeight: nd.headlineLineHeight,
                }}
            >
                {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#6021B3', textDecoration: 'underline' }}>
                        {item.headline}
                    </a>
                ) : (
                    item.headline
                )}
            </div>
            <div style={{ marginTop: 4, fontSize: ptToPx(nd.dateSize), fontFamily: SF.mono, color: c(nd.dateColor) }}>{item.date}</div>
        </div>
    );
};

const ScreenSectorMetric: React.FC<{ m: SectorMetric }> = ({ m }) => (
    <div
        style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#FFFFFF',
            border: `${ptToPx(nd.cardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(nd.cardRadius),
            paddingLeft: ptToPx(nd.sectorMetricPad),
            paddingRight: ptToPx(nd.sectorMetricPad),
            paddingTop: 8,
            paddingBottom: 8,
            minHeight: ptToPx(60),
        }}
    >
        <div
            style={{
                fontSize: ptToPx(nd.sectorMetricLabelSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: c(nd.sectorMetricLabelColor),
                letterSpacing: `${nd.sectorMetricLabelTrackingEm}em`,
                textTransform: 'uppercase',
            }}
        >
            {m.label}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginTop: 4 }}>
            <span
                style={{
                    fontSize: ptToPx(nd.sectorMetricValueSize),
                    fontFamily: SF.mono,
                    fontWeight: 500,
                    color: m.valueColor ?? c(nd.sectorMetricValueColor),
                }}
            >
                {m.value}
            </span>
            {m.unit ? (
                <span style={{ fontSize: ptToPx(nd.sectorMetricUnitSize), fontFamily: SF.mono, color: c(nd.sectorMetricUnitColor) }}>
                    {m.unit}
                </span>
            ) : null}
        </div>
        {m.sub ? (
            <div
                style={{
                    fontSize: ptToPx(nd.sectorMetricSubSize - 1),
                    fontFamily: SF.sans,
                    color: c(nd.sectorMetricSubColor),
                    marginTop: 'auto',
                    paddingTop: 4,
                    textAlign: 'right',
                }}
            >
                {m.sub}
            </div>
        ) : null}
    </div>
);

const ScreenDriverList: React.FC<{ items: string[]; header: string; tone: 'green' | 'red' }> = ({ items, header, tone }) => {
    const headerColor = tone === 'green' ? c(nd.drGreenColor) : c(nd.drRedColor);
    return (
        <div
            style={{
                flex: 1,
                background: c(nd.drCardBg),
                borderRadius: ptToPx(nd.drCardRadius),
                padding: ptToPx(nd.drCardPad),
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(nd.drHeaderSize),
                    fontFamily: SF.sans,
                    fontWeight: 500,
                    color: headerColor,
                    letterSpacing: `${nd.drHeaderTrackingEm}em`,
                    textTransform: 'uppercase',
                    marginBottom: 6,
                }}
            >
                {header}
            </div>
            {items.map((item, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        gap: 9,
                        paddingTop: ptToPx(nd.drListItemPadY),
                        paddingBottom: ptToPx(nd.drListItemPadY),
                        borderBottom: i < items.length - 1 ? `${ptToPx(nd.drListBorder)}px solid ${c(nd.drListBorderColor)}` : 'none',
                    }}
                >
                    <span style={{ fontSize: ptToPx(nd.drDashSize), fontFamily: SF.display, color: c(nd.drDashColor), lineHeight: 1 }}>
                        —
                    </span>
                    <span
                        style={{ flex: 1, fontSize: ptToPx(nd.drItemSize), fontFamily: SF.sans, color: c(nd.drItemColor), lineHeight: 1.5 }}
                    >
                        {item}
                    </span>
                </div>
            ))}
        </div>
    );
};

const ScreenCompetitorsCards: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cards = vm.cards;
    if (cards.length === 0) return null;
    // Screen-side tighter line-height for the name + description text. PDF
    // keeps the shared `cd.cardNameLineHeight` / `cd.cardDescLineHeight` tokens
    // because react-pdf renders at its own typographic metrics; the screen
    // renders larger and looser by default and needs to be reined in here.
    const screenNameLineHeight = 1.1;
    const screenDescLineHeight = 1.25;
    const nameMinHeight = ptToPx(cd.cardNameSize * screenNameLineHeight * cd.cardNameMaxLines);
    const descMinHeight = ptToPx(cd.cardDescSize * screenDescLineHeight * cd.cardDescMaxLines);
    const rows = pairsOfCards(cards);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: ptToPx(cd.cardGap) }}>
            {rows.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: ptToPx(cd.cardGap) }}>
                    {row.map((card, ci) => {
                        if (!card) return <div key={ci} style={{ flex: 1 }} />;
                        const subject = card.kind === 'subject';
                        const leftBorder = subject ? cd.cardSubjectBorderLeft : cd.cardBorder;
                        const padX = cd.cardPad - 1.5;
                        const padY = cd.cardPad - 2.5;
                        return (
                            <div
                                key={ci}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    background: subject ? c(cd.cardSubjectBg) : '#FFFFFF',
                                    borderTop: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderRight: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderBottom: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderLeft: `${ptToPx(leftBorder)}px solid ${
                                        subject ? c(cd.cardSubjectBorderColor) : c(cd.cardBorderColor)
                                    }`,
                                    borderRadius: ptToPx(cd.cardRadius),
                                    paddingTop: ptToPx(padY),
                                    paddingBottom: ptToPx(padY),
                                    paddingRight: ptToPx(padX),
                                    paddingLeft: ptToPx(subject ? padX + cd.cardSubjectBorderLeft - cd.cardBorder : padX),
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <div style={{ minHeight: nameMinHeight }}>
                                    <span
                                        style={{
                                            fontSize: ptToPx(cd.cardNameSize),
                                            fontFamily: SF.sans,
                                            fontWeight: 600,
                                            color: c(cd.cardNameColor),
                                            lineHeight: screenNameLineHeight,
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {card.name}
                                    </span>
                                </div>
                                {subject ? (
                                    <div
                                        style={{
                                            fontSize: ptToPx(cd.cardKickerSize),
                                            fontFamily: SF.sans,
                                            fontWeight: 500,
                                            color: c(cd.cardKickerSubjectColor),
                                            letterSpacing: `${cd.cardKickerTrackingEm}em`,
                                            textTransform: 'uppercase',
                                            marginTop: ptToPx(cd.cardKickerGap),
                                        }}
                                    >
                                        Subject Company
                                    </div>
                                ) : null}
                                <div style={{ marginTop: ptToPx(subject ? cd.cardDescGap : cd.cardKickerGap), minHeight: descMinHeight }}>
                                    {card.description ? (
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardDescSize),
                                                fontFamily: SF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: screenDescLineHeight,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {card.description}
                                        </span>
                                    ) : null}
                                    {card.truncated ? (
                                        <div
                                            style={{
                                                fontSize: ptToPx(cd.cardDescSize - 0.5),
                                                fontStyle: 'italic',
                                                color: overviewColors.inkMuted,
                                                marginTop: 4,
                                            }}
                                        >
                                            Truncated — see "About the Company" above for the full text.
                                        </div>
                                    ) : null}
                                </div>
                                <div
                                    style={{
                                        borderTop: `${ptToPx(cd.cardBorder)}px solid ${cd.cardDividerColor}`,
                                        marginTop: ptToPx(6),
                                    }}
                                >
                                    {(
                                        [
                                            { label: 'Founded', value: card.foundingYear },
                                            { label: 'Location', value: card.location },
                                            { label: 'Website', value: card.website, href: card.website },
                                        ] as Array<{ label: string; value?: string; href?: string }>
                                    )
                                        .filter((r) => !!r.value)
                                        .map((r, idx, arr) => (
                                            <div
                                                key={r.label}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    paddingTop: ptToPx(cd.cardRowPadY),
                                                    paddingBottom: ptToPx(cd.cardRowPadY),
                                                    borderBottom:
                                                        idx === arr.length - 1
                                                            ? 'none'
                                                            : `${ptToPx(cd.cardBorder)}px solid ${cd.cardDividerColor}`,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: ptToPx(cd.cardRowSize),
                                                        fontFamily: SF.sans,
                                                        color: c(cd.cardRowLabelColor),
                                                    }}
                                                >
                                                    {r.label}
                                                </span>
                                                {r.href ? (
                                                    <a
                                                        href={r.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            fontSize: ptToPx(cd.cardRowSize),
                                                            fontFamily: SF.mono,
                                                            fontWeight: 500,
                                                            color: overviewColors.accent,
                                                            textDecoration: 'none',
                                                        }}
                                                    >
                                                        {(r.value ?? '').replace(/^https?:\/\//i, '').replace(/\/$/, '')}
                                                    </a>
                                                ) : (
                                                    <span
                                                        style={{
                                                            fontSize: ptToPx(cd.cardRowSize),
                                                            fontFamily: SF.mono,
                                                            fontWeight: 500,
                                                            color: c(cd.cardRowValueColor),
                                                        }}
                                                    >
                                                        {r.value}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                </div>
                                {card.keyAchievements ? (
                                    <div style={{ marginTop: ptToPx(6) }}>
                                        <div
                                            style={{
                                                fontSize: ptToPx(cd.cardKickerSize),
                                                fontFamily: SF.sans,
                                                fontWeight: 600,
                                                color: c(cd.cardRowLabelColor),
                                                letterSpacing: `${cd.cardKickerTrackingEm}em`,
                                                textTransform: 'uppercase',
                                                marginBottom: ptToPx(3),
                                            }}
                                        >
                                            Key Achievements
                                        </div>
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardDescSize),
                                                fontFamily: SF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: cd.cardDescLineHeight,
                                            }}
                                        >
                                            {card.keyAchievements}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

// Legacy comparison table — kept in source so we can swap back via the
// render line below if the new card layout doesn't fit a future spec.
const ScreenCompetitorsTable: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cols = [vm.focal.name, ...vm.rivals.map((r) => r.name)];
    return (
        <div
            style={{
                borderTop: `${ptToPx(cd.headerBorder)}px solid ${c(cd.headerBorderColor)}`,
                borderBottom: `${ptToPx(cd.headerBorder)}px solid ${c(cd.headerBorderColor)}`,
            }}
        >
            <div style={{ display: 'flex' }}>
                <div style={{ width: ptToPx(cd.labelColWidth) }} />
                {cols.map((name, i) => {
                    const focal = i === 0;
                    return (
                        <div
                            key={i}
                            style={{
                                flex: 1,
                                background: focal ? c(cd.focalHeaderBg) : '#FFFFFF',
                                borderBottom: `${ptToPx(cd.headerBorder)}px solid ${
                                    focal ? c(cd.focalHeaderBorder) : c(cd.headerBorderColor)
                                }`,
                                paddingLeft: ptToPx(cd.cellPadX),
                                paddingRight: ptToPx(cd.cellPadX),
                                paddingTop: ptToPx(cd.cellPadY + 4),
                                paddingBottom: ptToPx(cd.cellPadY + 4),
                            }}
                        >
                            <span
                                style={{
                                    fontSize: ptToPx(cd.headerSize),
                                    fontFamily: SF.sans,
                                    fontWeight: 600,
                                    color: focal ? c(cd.headerFocalColor) : c(cd.headerColor),
                                    letterSpacing: `${cd.headerTrackingEm}em`,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {name}
                            </span>
                        </div>
                    );
                })}
            </div>
            {vm.rows.map((row) => (
                <div key={row.key} style={{ display: 'flex' }}>
                    <div
                        style={{
                            width: ptToPx(cd.labelColWidth),
                            paddingLeft: ptToPx(cd.cellPadX),
                            paddingRight: ptToPx(cd.cellPadX),
                            paddingTop: ptToPx(cd.cellPadY),
                            paddingBottom: ptToPx(cd.cellPadY),
                            borderBottom: `${ptToPx(cd.border)}px solid ${c(cd.borderColor)}`,
                        }}
                    >
                        <span
                            style={{
                                fontSize: ptToPx(cd.labelSize),
                                fontFamily: SF.sans,
                                fontWeight: 500,
                                color: c(cd.labelColor),
                                letterSpacing: `${cd.labelTrackingEm}em`,
                                textTransform: 'uppercase',
                            }}
                        >
                            {row.label}
                        </span>
                    </div>
                    {row.values.map((value, i) => {
                        const focal = i === 0;
                        const muted = value === 'Not disclosed';
                        return (
                            <div
                                key={i}
                                style={{
                                    flex: 1,
                                    background: focal ? c(cd.focalBg) : '#FFFFFF',
                                    paddingLeft: ptToPx(cd.cellPadX),
                                    paddingRight: ptToPx(cd.cellPadX),
                                    paddingTop: ptToPx(cd.cellPadY),
                                    paddingBottom: ptToPx(cd.cellPadY),
                                    borderBottom: `${ptToPx(cd.border)}px solid ${c(cd.borderColor)}`,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: ptToPx(cd.cellSize),
                                        fontFamily: row.mono ? SF.mono : SF.sans,
                                        color: muted ? c(cd.cellMutedColor) : c(cd.cellColor),
                                        lineHeight: cd.cellLineHeight,
                                    }}
                                >
                                    {value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const ScreenOverview: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    // See PdfOverview note: no early bail on missing `overview`. Section renders
    // whenever any sub-block has data (gated by `hasData` on the module).
    const vm = buildVM(data);

    return (
        <ScreenSection kicker="Company" title="Company Overview" index={index}>
            <ScreenIdentityCard vm={vm} />

            {vm.aboutBullets && vm.aboutBullets.length > 0 ? (
                <>
                    <ScreenH3>About the Company</ScreenH3>
                    {vm.aboutBullets.map((b, i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                gap: 6,
                                marginTop: i > 0 ? 4 : 0,
                                fontSize: ptToPx(od.bodySize),
                                fontFamily: SF.sans,
                                color: c(od.bodyColor),
                                lineHeight: od.bodyLineHeight,
                            }}
                        >
                            <span>•</span>
                            <span style={{ flex: 1 }}>{b}</span>
                        </div>
                    ))}
                </>
            ) : null}

            {vm.businessCards.length > 0 ? (
                <>
                    <ScreenH3>Business Profile</ScreenH3>
                    <div style={{ display: 'flex', gap: ptToPx(od.bpGap) }}>
                        {vm.businessCards.map((card) => (
                            <ScreenBusinessCard key={card.title} title={card.title} bullets={card.bullets} />
                        ))}
                    </div>
                </>
            ) : null}

            {vm.basicDetails.items.length > 0 ? (
                <>
                    <ScreenH3>Basic Details</ScreenH3>
                    <ScreenBasicDetails vm={vm.basicDetails} />
                </>
            ) : null}

            {vm.news.length > 0 ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <ScreenH3 topGap={0}>News &amp; Updates</ScreenH3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: ptToPx(nd.gridGap) }}>
                        {vm.news.map((n, i) => (
                            <ScreenNewsCard key={i} item={n} />
                        ))}
                    </div>
                </div>
            ) : null}

            {vm.sectorOutlook ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 8,
                            marginBottom: ptToPx(od.headingToBody),
                        }}
                    >
                        <span style={{ fontSize: ptToPx(od.h3Size), fontFamily: SF.sans, fontWeight: 600, color: c(od.h3Color) }}>
                            Sector / Industry Outlook
                        </span>
                        {vm.sectorOutlook.sectorName ? (
                            <span style={{ fontSize: ptToPx(7.5), fontFamily: SF.sans, color: overviewColors.inkMuted }}>
                                {vm.sectorOutlook.sectorName}
                            </span>
                        ) : null}
                    </div>
                    {vm.sectorOutlook.metrics.length > 0 ? (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${vm.sectorOutlook.metrics.length}, 1fr)`,
                                gap: ptToPx(nd.gridGap),
                                marginBottom: ptToPx(nd.sectorGap),
                            }}
                        >
                            {vm.sectorOutlook.metrics.map((m) => (
                                <ScreenSectorMetric key={m.label} m={m} />
                            ))}
                        </div>
                    ) : null}
                    {vm.sectorOutlook.body ? (
                        <div
                            style={{
                                fontSize: ptToPx(nd.sectorBodySize),
                                fontFamily: SF.sans,
                                color: c(nd.sectorBodyColor),
                                lineHeight: nd.sectorBodyLineHeight,
                                marginBottom: ptToPx(nd.sectorGap),
                            }}
                        >
                            {vm.sectorOutlook.body}
                        </div>
                    ) : null}
                    {vm.sectorOutlook.growthDrivers.length > 0 || vm.sectorOutlook.keyRisks.length > 0 ? (
                        <div style={{ display: 'flex', gap: ptToPx(nd.gridGap) }}>
                            {vm.sectorOutlook.growthDrivers.length > 0 ? (
                                <ScreenDriverList items={vm.sectorOutlook.growthDrivers} header="Growth Drivers" tone="green" />
                            ) : null}
                            {vm.sectorOutlook.keyRisks.length > 0 ? (
                                <ScreenDriverList items={vm.sectorOutlook.keyRisks} header="Key Risks" tone="red" />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {vm.competitors ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <ScreenH3 topGap={0}>Key Competitors</ScreenH3>
                    <ScreenCompetitorsCards vm={vm.competitors} />
                </div>
            ) : null}
            <ScreenOverviewMiniCharts data={data} />
        </ScreenSection>
    );
};

const ScreenOverviewMiniCharts: React.FC<{ data: ReportData }> = ({ data }) => {
    const emp = buildEmployeePoints(data);
    if (emp.length < 2) return null;
    return (
        <div style={{ marginTop: ptToPx(interSectionGap) }}>
            <div>
                <ScreenH3 topGap={0}>Employee Headcount</ScreenH3>
                <ScreenLineChart points={emp} height={140} />
                <div style={{ fontSize: '0.7rem', color: overviewColors.inkMuted, marginTop: 4 }}>
                    Monthly EPF-registered employees · last 18 months
                </div>
            </div>
        </div>
    );
};

export const overviewModule: ModuleDefinition = {
    meta: {
        id: 'overview',
        title: 'Company overview',
        description: 'Company identity, business profile, basic details, risk grade, news, sector outlook and competitors.',
        dataSource: 'Profile Overview, Sector Outlook, News, Competitors',
    },
    hasData: (d) => {
        if (!d.identity?.orgName) return false;
        const o = d.overview;
        const hasNarrative = !!(o?.overviewContent || o?.productOfferings || o?.customersAndSalesChannels || o?.geographiesSegmentation);
        const hasNews = (o?.news?.length ?? 0) > 0;
        const hasCompetitors = (o?.competitors?.length ?? 0) > 0;
        const hasSectorOutlook = !!(
            o?.sectorOverview ||
            o?.marketSize ||
            o?.industryCagr ||
            o?.marketGrowth ||
            o?.sectorRisks ||
            o?.growthDrivers?.length ||
            o?.keyRisks?.length
        );
        const hasHeadcount = (d.management?.momEmployee?.length ?? 0) >= 2;
        return hasNarrative || hasNews || hasCompetitors || hasSectorOutlook || hasHeadcount;
    },
    Pdf: PdfOverview,
    Screen: ScreenOverview,
};
